// This page is rendered by the root boundary, outside every route provider.
// Keep its own theme scope and avoid auth, router, network, or MUI dependencies.
import { useState } from 'react'

import {
  COLOR_MODE_STORAGE_KEY,
  COLOR_SCHEME_ATTRIBUTE,
  LIGHT_ONLY_PATH_PATTERN,
} from 'src/design-system/colorScheme'
import { UiButton as Button } from 'src/design-system/ui/components/UiButton'
import { UiStack as Stack } from 'src/design-system/ui/components/UiLayout'
import { UiText as Text } from 'src/design-system/ui/components/UiText'
import { UiProvider } from 'src/design-system/ui/UiProvider'
import type { UiColorMode } from 'src/design-system/ui/themes'

import './fatal-error-page.css'

/** Recover the appearance without assuming the crashed provider is available. */
const initialMode = (): UiColorMode => {
  if (typeof window === 'undefined' || typeof document === 'undefined')
    return 'system'

  // Applicant routes own a light-only canvas, even when the saved preference
  // for Professional/legacy pages is dark.
  if (LIGHT_ONLY_PATH_PATTERN.test(window.location.pathname)) return 'light'

  let preference: string | null = null
  try {
    preference = window.localStorage.getItem(COLOR_MODE_STORAGE_KEY)
  } catch {
    // Storage can be unavailable; the rendered document remains authoritative.
  }
  if (
    preference === 'light' ||
    preference === 'dark' ||
    preference === 'system'
  )
    return preference

  const root = document.documentElement
  const inlineScheme = root.style.colorScheme
  const attributeScheme = root.getAttribute(COLOR_SCHEME_ATTRIBUTE)
  const scheme =
    inlineScheme === 'light' || inlineScheme === 'dark'
      ? inlineScheme
      : attributeScheme === 'light' || attributeScheme === 'dark'
        ? attributeScheme
        : undefined
  try {
    const system = window.matchMedia?.('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light'
    // If storage is blocked and a user's unsaved selection differs from
    // the system, preserve the document appearance they were using.
    return scheme && scheme !== system ? scheme : 'system'
  } catch {
    return scheme ?? 'system'
  }
}

const FatalErrorPage = () => {
  const [mode] = useState(initialMode)
  return (
    <UiProvider theme="professional" mode={mode} className="fatal-error-scope">
      <main className="fatal-error-page">
        <section className="fatal-error-content">
          <Stack gap="loose">
            <Stack gap="tight">
              <Text as="h1" variant="heading">
                Something went wrong
              </Text>
              <Text tone="muted">Please reload the page to try again.</Text>
            </Stack>
            <div>
              <Button onPress={() => window.location.reload()}>
                Reload page
              </Button>
            </div>
          </Stack>
        </section>
      </main>
    </UiProvider>
  )
}

export default FatalErrorPage
